import { Field, FieldArray, Form, Formik, FieldProps } from "formik";
import React, { useEffect, useState } from "react";
import { reactSelectStyle } from "../../../Offers/components/Form/OfferForm";
import type { SelectValues } from "../../../Offers/components/Form/OfferForm";
import { get } from "../../../../_api";
import { IBlog, IBlogAuthor } from "../../Blog";
import ReactSelect from "react-select";
import Calendar from "react-calendar";
import Paragraph from "../Paragraphs/Paragraph";
import {
  Box,
  Button,
  Checkbox,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import MediaPicker from "../../../Media/MediaPicker";

type Props = {
  onSubmit(values: unknown): void;
  blog?: IBlog;
};

const mapBlogAuthorsToSelectValues = (
  authors: IBlogAuthor[]
): SelectValues[] => {
  return authors.map((el) => ({ label: el.name, value: el._id }));
};

const categories = [
  "Prawo w biznesie",
  "Prawo pracy",
  "Prawo cywilne",
  "Prawo podatkowe",
  "Prawo upadlosciowe",
  "Nowe technologie",
];

/** API may send `quotes` as string[]; the form uses `{ text: string }[]` for inputs. */
function normalizeQuoteForForm(q: unknown): { text: string } {
  if (typeof q === "string") return { text: q };
  if (q && typeof q === "object" && "text" in (q as object)) {
    const t = (q as { text?: unknown }).text;
    return { text: t == null ? "" : String(t) };
  }
  return { text: "" };
}

function normalizeParagraphsForForm(paragraphs: IBlog["paragraphs"] | undefined) {
  if (!paragraphs?.length) {
    return [{ text: "", title: "", quotes: [] as { text: string }[] }];
  }
  return paragraphs.map((p) => ({
    text: p.text ?? "",
    title: p.title ?? "",
    quotes: (p.quotes ?? []).map(normalizeQuoteForForm),
  }));
}

function metaKeyWordsForTextarea(kw: IBlog["meta"]["keyWords"]): string {
  if (kw == null) return "";
  if (Array.isArray(kw)) return kw.join(", ");
  return String(kw);
}

export const BlogForm: React.FC<Props> = ({ onSubmit, blog }) => {
  const [authors, setAuthors] = useState<IBlogAuthor[]>([]);
  useEffect(() => {
    get("auth/authors").then((data) => {
      if (data) {
        setAuthors(data);
      }
    });
  }, []);
  return (
    <Formik
      onSubmit={(values) => {
        const v = values as {
          meta?: { keyWords?: string | string[] } & Record<string, unknown>;
        } & Record<string, unknown>;
        const rawKw = v.meta?.keyWords;
        const keyWords =
          typeof rawKw === "string"
            ? rawKw
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : rawKw;
        onSubmit({
          ...v,
          meta: v.meta ? { ...v.meta, keyWords } : v.meta,
        });
      }}
      enableReinitialize={true}
      initialValues={{
        image: blog?.image || "",
        imageTitle: blog?.imageTitle || "",
        authors: blog?.authors || [],
        categories: blog?.categories || [],
        headerTitle: blog?.headerTitle || "",
        shortDescription: blog?.shortDescription || "",
        slug: blog?.slug || "",
        promoted: blog?.promoted || false,
        meta: {
          title: blog?.meta.title || "",
          readTime: blog?.meta.readTime || 10,
          keyWords: metaKeyWordsForTextarea(blog?.meta.keyWords),
          description: blog?.meta.description || "",
          any: blog?.meta.any || undefined,
          createdDate: blog?.meta.createdDate || new Date(),
        },
        paragraphs: normalizeParagraphsForForm(blog?.paragraphs),
      }}
    >
      {(props) => (
        <Form>
          <Stack gap="md">
            <Field name="headerTitle">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Tytuł"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="shortDescription">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  label="Krótki opis (kafelek)"
                  rows={5}
                  resize="vertical"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="slug">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Slug"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="imageTitle">
              {({ field, form }: FieldProps) => (
                <MediaPicker
                  label="Zdjęcie artykuł"
                  value={field.value ?? ""}
                  onChange={(url) => form.setFieldValue(field.name, url)}
                />
              )}
            </Field>
            <Field name="image">
              {({ field, form }: FieldProps) => (
                <MediaPicker
                  label="Zdjęcie kafelek"
                  value={field.value ?? ""}
                  onChange={(url) => form.setFieldValue(field.name, url)}
                />
              )}
            </Field>
            <Field name="promoted">
              {({ field, form }: FieldProps) => (
                <Checkbox
                  label="Promowane"
                  checked={field.value}
                  onChange={(e) =>
                    form.setFieldValue(field.name, e.currentTarget.checked)
                  }
                />
              )}
            </Field>

            <Text size="sm" fw={500}>
              Autorzy
            </Text>
            <Box>
              <ReactSelect
                isMulti
                styles={reactSelectStyle}
                value={mapBlogAuthorsToSelectValues(props.values.authors)}
                onChange={(newValue) => {
                  if (newValue) {
                    props.setFieldValue(
                      "authors",
                      newValue.map((el) => ({ _id: el.value, name: el.label }))
                    );
                  }
                }}
                options={mapBlogAuthorsToSelectValues(authors)}
              />
            </Box>

            <Text size="sm" fw={500}>
              Kategorie
            </Text>
            <Box>
              <ReactSelect
                isMulti
                styles={reactSelectStyle}
                value={props.values.categories.map((el: string) => ({
                  value: el,
                  label: el,
                }))}
                onChange={(newValue) => {
                  if (newValue) {
                    props.setFieldValue(
                      "categories",
                      newValue.map((el) => el.value)
                    );
                  }
                }}
                options={categories.map((el) => ({
                  value: el,
                  label: el,
                }))}
              />
            </Box>

            <Title order={3}>Meta dane</Title>

            <Field name="meta.title">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Tytuł (meta)"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="meta.keyWords">
              {({ field, meta }: FieldProps) => (
                <Textarea
                  label="Słowa kluczowe"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="meta.description">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Opis (description)"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>
            <Field name="meta.readTime">
              {({ field, meta }: FieldProps) => (
                <TextInput
                  label="Czas czytania (min)"
                  {...field}
                  error={meta.touched ? meta.error : undefined}
                />
              )}
            </Field>

            <Text size="sm" fw={500}>
              Data dodania
            </Text>
            <Box>
              <Calendar
                selectRange={false}
                onChange={(value: Date | Date[]) => {
                  let newDate: Date;
                  if (Array.isArray(value)) {
                    newDate = new Date(value[0]);
                  } else {
                    newDate = new Date(value);
                  }
                  newDate.setHours(0);
                  newDate.setMinutes(0);
                  newDate.setSeconds(0);

                  props.setFieldValue("meta.createdDate", newDate);
                }}
              />
            </Box>

            <Title order={3}>Paragrafy</Title>

            <FieldArray name="paragraphs">
              {({ remove, push }) => {
                return (
                  <>
                    {props.values.paragraphs.map((p, i) => (
                      <Paragraph
                        key={i}
                        index={i}
                        onRemove={() => {
                          remove(i);
                        }}
                      />
                    ))}
                    <Button
                      type="button"
                      variant="light"
                      color="orange"
                      size="sm"
                      radius="md"
                      onClick={() => {
                        push({
                          title: "",
                          text: "",
                          quotes: [],
                        });
                      }}
                    >
                      Dodaj paragraf
                    </Button>
                  </>
                );
              }}
            </FieldArray>

            <Button type="submit" size="md" radius="md" fullWidth mt="md">
              {blog ? "Aktualizuj artykuł" : "Dodaj artykuł"}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
